import { Directive, effect, EmbeddedViewRef, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { ConfigService } from '../../config.service';
import { FeatureFlag } from '../models/feature-flags.model';


/**
 * A structural directive that conditionally renders content based on
 * if the passed feature flags are enabled. If no flags are passed renders it's template.
 *
 * Type checks against a list of all feature names in `feature-flags.model`
 *
 * Single Flag Example:
 * ```html
 * <div *featureIf="['new-feature']">
 * ```
 *
 * Multiple Flags Example:
 * ```html
 * <div *featureIf="['new-feature', 'other-feature']">
 * ```
 */
@Directive({
  selector: '[featureIf]',
  standalone: true,
})
export class FeatureIfDirective {
  private templateRef = inject(TemplateRef);
  private viewContainerRef = inject(ViewContainerRef);
  private configService = inject(ConfigService);
  private embeddedViewRef: EmbeddedViewRef<unknown>;

  featureIf = input([], { transform: this.parsePassedFlags });
  featureIfType = input<'any' | 'all'>('all');

  parsePassedFlags(input: FeatureFlag[] | FeatureFlag | undefined): FeatureFlag[] {
    if (!input) {
      return [];
    }

    return (typeof input === 'string') ? [input] : input;
  }

  updateTemplate = effect(() => {

    const featuresToCheck = this.featureIf();

    const allEnabled = featuresToCheck.every(feature => this.configService.isFeatureEnabled(feature))
    const someEnabled = featuresToCheck.some(feature => this.configService.isFeatureEnabled(feature))

    const checkPassed = this.featureIfType() === 'all' ? allEnabled : someEnabled;

    const shouldRender = checkPassed || featuresToCheck.length === 0;

    if (shouldRender) {
      const embeddedViewAlreadyCreated = Boolean(this.embeddedViewRef);

      if (embeddedViewAlreadyCreated) {
        this.embeddedViewRef.detectChanges();
      } else {
        this.embeddedViewRef = this.viewContainerRef.createEmbeddedView(this.templateRef);
      }
    }
    else {
      this.viewContainerRef.clear();
    }
  }, { allowSignalWrites: true })
}
